import React, { useCallback } from "react";
import { useToast } from "@/context/ToastContext";
import { useSoftphone, type CallTarget } from "./SoftphoneContext";

type Kind = CallTarget["type"];

/**
 * Calls a record from the CRM through the softphone (Plivo). There is no fallback to the device's own dialer (a tel: link):
 * if the softphone is not switched on for the user, they are told how to get it. The server looks the number up from the record.
 */
export function useCallRecord() {
  const sp = useSoftphone();
  const { error } = useToast();
  return useCallback(
    async (type: Kind, id: string, _number?: string | null, field?: CallTarget["field"]) => {
      if (sp.phase === "off") {
        // Said as a message inside the CRM (never the browser's phone app), and says who can fix it
        if (sp.offReason === "restricted") return void error("You can't make calls", "Calling is restricted for your account. Ask the Super Admin to allow it.");
        if (sp.offReason === "not_enabled") return void error("Calling isn't switched on for you", "Ask the Super Admin to switch calling on for your user.");
        return void error("Calling isn't set up yet", "The Super Admin needs to finish the calling setup (Settings → Calling).");
      }
      if (sp.phase === "connecting") return void error("Calling is signing in", "Try again in a few seconds.");
      if (sp.phase === "offline") return void error("Calling is offline", "Open Settings → My calling to see why.");
      if (sp.phase === "incoming") return void error("A call is ringing", "Answer or decline it first.");
      if (sp.phase !== "ready" && sp.phase !== "ended") return void error("You're already on a call", "Finish it before starting another.");
      try {
        await sp.startCall({ type, id, field });
      } catch (err: any) {
        error("Couldn't start the call", err?.message || "Please try again.");
      }
    },
    [sp, error],
  );
}

interface CallLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick"> {
  type: Kind;
  id: string;
  number?: string | null;
  field?: CallTarget["field"];
}

/** A phone number (or any content) that starts a CRM call when clicked */
export const CallLink = React.forwardRef<HTMLAnchorElement, CallLinkProps>(function CallLink({ type, id, number, field, children, ...rest }, ref) {
  const callRecord = useCallRecord();
  const start = () => void callRecord(type, id, number, field);
  return (
    // No href: a tel: link would hand the number to the browser's own phone app, and calls are made from the CRM
    <a
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={(e) => { e.preventDefault(); start(); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); start(); } }}
      {...rest}
      className={`cursor-pointer ${rest.className || ""}`}
    >
      {children}
    </a>
  );
});
